import React, { useEffect, useMemo, useState, useCallback } from 'react';
import CenteredLoadingSpinner from '../../components/CenteredLoadingSpinner';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import type { SnackbarVariant } from '../../components/Snackbar';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import ResearchNoteModal from './ResearchNoteModal';
import FloatingNewNoteButton from './FloatingNewNoteButton';
import {
  fetchFiscalStandardizedIncomeStatement,
  fetchFiscalStandardizedIncomeStatementTtm,
  fetchFiscalStandardizedMetricsList,
  fetchFiscalEffectiveTaxRateRatios,
  fetchFiscalIncomeStatementAsReportedFilingIds,
  fetchFiscalFilingPdfBlobUrl,
  fetchSecFilingUrlForYear,
  fetchSecFilingUrlForReportDate,
  type FiscalIncomeRow,
  type FiscalRatioRow,
} from '../../services/fiscalApi';
import {
  fetchIncomeStatement,
  fetchIncomeStatementTTM,
  fetchRatios,
  type IncomeStatement,
  type Ratios,
} from '../../services/stockApi';

interface ResearchIncomeStatementFiscalScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
  selectedPeriod: string;
  isAdmin?: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

const makeIncomeStatementBase = (symbol: string, date: string, period: string): IncomeStatement => {
  const fiscalYear = date ? String(new Date(date).getUTCFullYear()) : '';
  return {
    date,
    symbol,
    reportedCurrency: 'USD',
    cik: '',
    filingDate: '',
    acceptedDate: '',
    fiscalYear,
    period,
    revenue: 0,
    costOfRevenue: 0,
    grossProfit: 0,
    researchAndDevelopmentExpenses: 0,
    generalAndAdministrativeExpenses: 0,
    sellingAndMarketingExpenses: 0,
    sellingGeneralAndAdministrativeExpenses: 0,
    otherExpenses: 0,
    operatingExpenses: 0,
    costAndExpenses: 0,
    netInterestIncome: 0,
    interestIncome: 0,
    interestExpense: 0,
    depreciationAndAmortization: 0,
    ebitda: 0,
    ebit: 0,
    nonOperatingIncomeExcludingInterest: 0,
    operatingIncome: 0,
    totalOtherIncomeExpensesNet: 0,
    incomeBeforeTax: 0,
    incomeTaxExpense: 0,
    netIncomeFromContinuingOperations: 0,
    netIncomeFromDiscontinuedOperations: 0,
    otherAdjustmentsToNetIncome: 0,
    netIncome: 0,
    netIncomeDeductions: 0,
    bottomLineNetIncome: 0,
    eps: 0,
    epsDiluted: 0,
    weightedAverageShsOut: 0,
    weightedAverageShsOutDil: 0,
  };
};

const mapFiscalIncomeRow = (row: FiscalIncomeRow, symbol: string, period: 'annual' | 'quarter' | 'ttm'): IncomeStatement => {
  const date = row.date || '';
  const statement = makeIncomeStatementBase(symbol, date, period === 'annual' ? 'FY' : period === 'quarter' ? 'Q' : 'TTM');
  const empty = Number.NaN;

  statement.costOfRevenue = empty;
  statement.researchAndDevelopmentExpenses = empty;
  statement.generalAndAdministrativeExpenses = empty;
  statement.sellingAndMarketingExpenses = empty;
  statement.sellingGeneralAndAdministrativeExpenses = empty;
  statement.otherExpenses = empty;
  statement.operatingExpenses = empty;
  statement.costAndExpenses = empty;
  statement.netInterestIncome = empty;
  statement.interestIncome = empty;
  statement.interestExpense = empty;
  statement.depreciationAndAmortization = empty;
  statement.ebitda = empty;
  statement.ebit = empty;
  statement.nonOperatingIncomeExcludingInterest = empty;
  statement.totalOtherIncomeExpensesNet = empty;
  statement.incomeBeforeTax = empty;
  statement.incomeTaxExpense = empty;
  statement.netIncomeFromContinuingOperations = empty;
  statement.netIncomeFromDiscontinuedOperations = empty;
  statement.otherAdjustmentsToNetIncome = empty;
  statement.netIncomeDeductions = empty;
  statement.bottomLineNetIncome = empty;
  statement.weightedAverageShsOut = empty;
  statement.weightedAverageShsOutDil = empty;

  statement.revenue = row.revenue ?? empty;
  statement.costOfRevenue = row.costOfRevenue ?? empty;
  statement.grossProfit = row.grossProfit ?? empty;
  statement.researchAndDevelopmentExpenses = row.researchAndDevelopmentExpenses ?? empty;
  statement.sellingGeneralAndAdministrativeExpenses = row.sellingGeneralAndAdministrativeExpenses ?? empty;
  statement.operatingExpenses = row.operatingExpenses ?? empty;
  statement.interestIncome = row.interestIncome ?? empty;
  statement.netInterestIncome = row.netInterestIncome ?? empty;
  statement.interestExpense = row.interestExpense ?? empty;
  statement.incomeBeforeTax = row.incomeBeforeTax ?? empty;
  statement.incomeTaxExpense = row.incomeTaxExpense ?? empty;
  statement.nonOperatingIncomeExcludingInterest = row.nonOperatingIncome ?? empty;
  statement.totalOtherIncomeExpensesNet = row.nonOperatingIncome ?? empty;
  statement.operatingIncome = row.operatingIncome ?? empty;
  statement.netIncome = row.netIncome ?? empty;
  statement.epsDiluted = row.epsDiluted ?? row.eps ?? empty;
  statement.eps = statement.epsDiluted;
  statement.weightedAverageShsOutDil = row.dilutedWeightedAverageSharesOutstanding ?? empty;
  return statement;
};

const buildFiscalIncomeTtmFromQuarters = (rows: IncomeStatement[], symbol: string): IncomeStatement | null => {
  const sortedDesc = [...rows]
    .filter((row) => row?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const latest4 = sortedDesc.slice(0, 4);
  if (latest4.length < 4) return null;

  const sum4 = (getter: (row: IncomeStatement) => number) => {
    const values = latest4.map(getter);
    if (values.some((v) => !Number.isFinite(v))) return Number.NaN;
    return values.reduce((acc, value) => acc + (value as number), 0);
  };

  const ttm = makeIncomeStatementBase(symbol, latest4[0]?.date ?? '', 'TTM');

  ttm.revenue = sum4((row) => row.revenue);
  ttm.costOfRevenue = sum4((row) => row.costOfRevenue);
  ttm.grossProfit = sum4((row) => row.grossProfit);
  ttm.researchAndDevelopmentExpenses = sum4((row) => row.researchAndDevelopmentExpenses);
  ttm.sellingGeneralAndAdministrativeExpenses = sum4((row) => row.sellingGeneralAndAdministrativeExpenses);
  ttm.operatingExpenses = sum4((row) => row.operatingExpenses);
  ttm.interestIncome = sum4((row) => row.interestIncome);
  ttm.netInterestIncome = sum4((row) => row.netInterestIncome);
  ttm.interestExpense = sum4((row) => row.interestExpense);
  ttm.operatingIncome = sum4((row) => row.operatingIncome);
  ttm.incomeBeforeTax = sum4((row) => row.incomeBeforeTax);
  ttm.incomeTaxExpense = sum4((row) => row.incomeTaxExpense);
  ttm.totalOtherIncomeExpensesNet = sum4((row) => row.totalOtherIncomeExpensesNet);
  ttm.nonOperatingIncomeExcludingInterest = sum4((row) => row.nonOperatingIncomeExcludingInterest);
  ttm.netIncome = sum4((row) => row.netIncome);

  const ttmEps = sum4((row) => row.epsDiluted);
  ttm.epsDiluted = ttmEps;
  ttm.eps = ttmEps;

  const latestShares = latest4.find((row) => Number.isFinite(row.weightedAverageShsOutDil))?.weightedAverageShsOutDil;
  ttm.weightedAverageShsOutDil = Number.isFinite(latestShares) ? (latestShares as number) : Number.NaN;

  return ttm;
};

const ResearchIncomeStatementFiscalScreen: React.FC<ResearchIncomeStatementFiscalScreenProps> = ({
  isDarkMode,
  selectedStock,
  selectedPeriod,
  isAdmin,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const [annualData, setAnnualData] = useState<IncomeStatement[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<IncomeStatement[]>([]);
  const [ttmData, setTtmData] = useState<IncomeStatement | null>(null);
  const [annualRatios, setAnnualRatios] = useState<Ratios[]>([]);
  const [quarterRatios, setQuarterRatios] = useState<Ratios[]>([]);
  const [fiscalTaxRatios, setFiscalTaxRatios] = useState<FiscalRatioRow[]>([]);
  const [fiscalAnnualFilingIdByLabel, setFiscalAnnualFilingIdByLabel] = useState<Record<string, string>>({});
  const [fiscalAnnualFilingIdByYear, setFiscalAnnualFilingIdByYear] = useState<Record<number, string>>({});
  const [fiscalQuarterFilingIdByLabel, setFiscalQuarterFilingIdByLabel] = useState<Record<string, string>>({});
  const [dataSource, setDataSource] = useState<'fiscal' | 'fmp'>('fiscal');
  const [loading, setLoading] = useState(true);

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCategory, setNoteCategory] = useState('Income Statement');
  const [noteContent, setNoteContent] = useState('');

  const [showFloatingButton, setShowFloatingButton] = useState<boolean>(true);

  useEffect(() => {
    const update = () => setShowFloatingButton(window.innerWidth >= 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const canPersist =
    !!authToken &&
    typeof authUserId === 'number' &&
    Number.isFinite(authUserId) &&
    authUserId > 0;
  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const hasNoteText = normalizeHtmlText(noteContent).length > 0;
  const canAddNote = noteCategory.trim().length > 0 && hasNoteText && noteTitle.trim().length > 0;

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  const openNewNote = () => {
    setNoteCategory('Income Statement');
    setNoteTitle('');
    setNoteContent('');
    setIsNoteModalOpen(true);
  };

  useEffect(() => {
    loadIncomeStatementData();
  }, [selectedStock, selectedPeriod]);

  useEffect(() => {
    let cancelled = false;

    const formatLabel = (dateStr: string): string => {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr;
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      return `${month} ${year}`;
    };

    const buildLabelMap = (rows: Array<{ filingId?: string; date?: string }>) => {
      const map: Record<string, string> = {};
      rows.forEach((row) => {
        const id = String(row.filingId ?? '').trim();
        const date = String(row.date ?? '').trim();
        if (!id || !date) return;
        const label = formatLabel(date);
        if (!map[label]) map[label] = id;
      });
      return map;
    };

    const buildYearMap = (rows: Array<{ filingId?: string; date?: string }>) => {
      const map: Record<number, string> = {};
      rows.forEach((row) => {
        const id = String(row.filingId ?? '').trim();
        const date = String(row.date ?? '').trim();
        if (!id || !date) return;
        const year = new Date(date).getFullYear();
        if (!Number.isFinite(year)) return;
        if (!map[year]) map[year] = id;
      });
      return map;
    };

    (async () => {
      try {
        const result = await fetchFiscalIncomeStatementAsReportedFilingIds(selectedStock);
        if (cancelled) return;
        const hasData = (result.annual?.length ?? 0) + (result.quarter?.length ?? 0) > 0;
        if (hasData) {
          setFiscalAnnualFilingIdByLabel(buildLabelMap(result.annual ?? []));
          setFiscalAnnualFilingIdByYear(buildYearMap(result.annual ?? []));
          setFiscalQuarterFilingIdByLabel(buildLabelMap(result.quarter ?? []));
          return;
        }

        const fallback = await fetchFiscalStandardizedIncomeStatement(selectedStock);
        if (cancelled) return;
        setFiscalAnnualFilingIdByLabel(buildLabelMap(fallback.annual ?? []));
        setFiscalAnnualFilingIdByYear(buildYearMap(fallback.annual ?? []));
        setFiscalQuarterFilingIdByLabel(buildLabelMap(fallback.quarter ?? []));
      } catch {
        if (!cancelled) {
          setFiscalAnnualFilingIdByLabel({});
          setFiscalAnnualFilingIdByYear({});
          setFiscalQuarterFilingIdByLabel({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedStock]);

  const openFiscalFilingPdfById = useCallback(
    async (filingId?: string) => {
      const safeId = String(filingId ?? '').trim();
      if (!safeId) {
        onNotify?.('Missing filing ID for this year.', 'error');
        return;
      }

      const blobUrl = await fetchFiscalFilingPdfBlobUrl(selectedStock, safeId);
      if (!blobUrl) {
        onNotify?.('Unable to load filing PDF. Please login and try again.', 'error');
        return;
      }

      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    },
    [onNotify, selectedStock]
  );

  const openAnnualFilingForYear = useCallback(
    async (year: number, fallbackId?: string) => {
      if (!Number.isFinite(year)) {
        onNotify?.('Missing filing year.', 'error');
        return;
      }

      const secUrl = await fetchSecFilingUrlForYear(selectedStock, year, '10-K');
      if (secUrl) {
        window.open(secUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (fallbackId) {
        await openFiscalFilingPdfById(fallbackId);
        return;
      }

      onNotify?.('Unable to load annual filing for this year.', 'error');
    },
    [onNotify, openFiscalFilingPdfById, selectedStock]
  );

  const openQuarterFilingForDate = useCallback(
    async (reportDate: string, fallbackId?: string, form: string = '10-Q') => {
      const rawDate = String(reportDate ?? '').trim();
      if (!rawDate) {
        onNotify?.('Missing filing date.', 'error');
        return;
      }

      const normalizedDate = (() => {
        const match = rawDate.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) return match[0];
        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) return rawDate;
        return parsed.toISOString().slice(0, 10);
      })();

      const secUrl = await fetchSecFilingUrlForReportDate(selectedStock, normalizedDate, form);
      if (secUrl) {
        window.open(secUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (fallbackId) {
        await openFiscalFilingPdfById(fallbackId);
        return;
      }

      onNotify?.('Unable to load quarterly filing for this date.', 'error');
    },
    [onNotify, openFiscalFilingPdfById, selectedStock]
  );

  const loadIncomeStatementData = async () => {
    setLoading(true);
    try {
      const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

      const mergeIncomeRowsWithFmpFallback = (fiscalRows: IncomeStatement[], fmpRows: IncomeStatement[]): IncomeStatement[] => {
        if (!fiscalRows.length || !fmpRows.length) return fiscalRows;
        const fmpByDate = new Map<string, IncomeStatement>();
        fmpRows.forEach((row) => {
          const key = String(row.date ?? '').trim();
          if (key && !fmpByDate.has(key)) fmpByDate.set(key, row);
        });

        return fiscalRows.map((row) => {
          const key = String(row.date ?? '').trim();
          if (!key) return row;
          const base = fmpByDate.get(key);
          if (!base) return row;

          return {
            ...row,
            grossProfit: isFiniteNumber(row.grossProfit) ? row.grossProfit : base.grossProfit,
            operatingIncome: isFiniteNumber(row.operatingIncome) ? row.operatingIncome : base.operatingIncome,
          };
        });
      };

      const mergeTtmWithFmpFallback = (fiscalRow: IncomeStatement | null, fmpRow: IncomeStatement | null): IncomeStatement | null => {
        if (!fiscalRow) return fmpRow;
        if (!fmpRow) return fiscalRow;
        return {
          ...fiscalRow,
          grossProfit: isFiniteNumber(fiscalRow.grossProfit) ? fiscalRow.grossProfit : fmpRow.grossProfit,
          operatingIncome: isFiniteNumber(fiscalRow.operatingIncome) ? fiscalRow.operatingIncome : fmpRow.operatingIncome,
        };
      };

      const metrics = await fetchFiscalStandardizedMetricsList();
      const [income, ttm, taxRatios] = await Promise.all([
        fetchFiscalStandardizedIncomeStatement(selectedStock, metrics),
        fetchFiscalStandardizedIncomeStatementTtm(selectedStock, metrics),
        fetchFiscalEffectiveTaxRateRatios(selectedStock),
      ]);

      const fiscalCount = income.annual.length + income.quarter.length;
      const errorText = String(income.error || ttm.error || '').trim();
      const unavailableByMessage = Boolean(errorText) && /data is not yet available|companies-list|companies list/i.test(errorText);
      const fiscalUnavailable = unavailableByMessage || fiscalCount === 0;

      if (!fiscalUnavailable) {
        const [fmpAnnual, fmpQuarterly, fmpTtm] = await Promise.all([
          fetchIncomeStatement(selectedStock, 'annual', 15),
          fetchIncomeStatement(selectedStock, 'quarter', 15),
          fetchIncomeStatementTTM(selectedStock, 1),
        ]);

        const annualFiscal = income.annual.map((row) => mapFiscalIncomeRow(row, selectedStock, 'annual'));
        const quarterlyFiscal = income.quarter.map((row) => mapFiscalIncomeRow(row, selectedStock, 'quarter'));
        const annual = mergeIncomeRowsWithFmpFallback(annualFiscal, fmpAnnual);
        const quarterly = mergeIncomeRowsWithFmpFallback(quarterlyFiscal, fmpQuarterly);
        const ttmFromQuarters = buildFiscalIncomeTtmFromQuarters(quarterly, selectedStock);
        const ttmFromApi = ttm.row ? mapFiscalIncomeRow(ttm.row, selectedStock, 'ttm') : null;
        const ttmRow = mergeTtmWithFmpFallback(ttmFromQuarters ?? ttmFromApi, fmpTtm);

        setAnnualData(annual);
        setQuarterlyData(quarterly);
        setTtmData(ttmRow);
        setAnnualRatios([]);
        setQuarterRatios([]);
        setFiscalTaxRatios(taxRatios);
        setDataSource('fiscal');
      } else {
        const [annual, quarterly, ttmRow, ratiosA, ratiosQ] = await Promise.all([
          fetchIncomeStatement(selectedStock, 'annual', 15),
          fetchIncomeStatement(selectedStock, 'quarter', 15),
          fetchIncomeStatementTTM(selectedStock, 1),
          fetchRatios(selectedStock, 'annual', 15),
          fetchRatios(selectedStock, 'quarter', 15),
        ]);

        setAnnualData(annual);
        setQuarterlyData(quarterly);
        setTtmData(ttmRow);
        setAnnualRatios(ratiosA);
        setQuarterRatios(ratiosQ);
        setFiscalTaxRatios([]);
        setDataSource('fmp');
      }
    } catch (error) {
      console.error('Error loading income statement fiscal data:', error);
    }
    setLoading(false);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';

    const absValue = Math.abs(value);
    if (absValue >= 1e9) {
      return (value / 1e9).toFixed(2) + 'B';
    } else if (absValue >= 1e6) {
      return (value / 1e6).toFixed(2) + 'M';
    }
    return value.toFixed(2);
  };

  const formatNumberAbs = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return formatNumber(Math.abs(value));
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} ${year}`;
  };

  const formatPercentMaybeRatio = (value: number | undefined | null): string => {
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '-';
    const asPercent = value > 1 ? value : value * 100;
    return `${asPercent.toFixed(2)}%`;
  };

  const formatPercentFromRatio = (ratio: number | undefined | null): string => {
    if (typeof ratio !== 'number' || Number.isNaN(ratio) || !Number.isFinite(ratio)) return '-';
    return `${(ratio * 100).toFixed(2)}%`;
  };

  const withNegativeColor = (base: React.CSSProperties, renderedText: string): React.CSSProperties => {
    if (typeof renderedText !== 'string') return base;
    const trimmed = renderedText.trim();
    if (trimmed.startsWith('-')) return { ...base, color: '#F44336' };
    return base;
  };

  const getRatioByDate = (date: string, period: 'annual' | 'quarter') => {
    const arr = period === 'annual' ? annualRatios : quarterRatios;
    const exact = arr.find((r) => r.date === date);
    if (exact) return exact;
    const year = new Date(date).getFullYear();
    return arr.find((r) => r.date && new Date(r.date).getFullYear() === year);
  };

  const fiscalTaxAnnual = useMemo(() => fiscalTaxRatios.filter((r) => r.periodType === 'annual'), [fiscalTaxRatios]);
  const fiscalTaxQuarter = useMemo(() => fiscalTaxRatios.filter((r) => r.periodType === 'quarter'), [fiscalTaxRatios]);
  const fiscalTaxLtm = useMemo(() => fiscalTaxRatios.filter((r) => r.periodType === 'ltm'), [fiscalTaxRatios]);

  const getFiscalTaxRateByDate = (date: string, period: 'annual' | 'quarter' | 'ltm') => {
    const source = period === 'annual' ? fiscalTaxAnnual : period === 'ltm' ? fiscalTaxLtm : fiscalTaxQuarter;
    if (!date) return undefined;
    const exact = source.find((r) => r.date === date);
    if (exact?.value != null) return exact.value;
    const targetLabel = formatDate(date);
    const sameLabel = source.find((r) => r.date && formatDate(r.date) === targetLabel);
    return sameLabel?.value;
  };

  const isFiscalSource = dataSource === 'fiscal';
  const showSourceLabels = Boolean(isAdmin);

  const formatPercentFromValue = (num: number | undefined, denom: number | undefined) => {
    if (typeof num !== 'number' || typeof denom !== 'number' || denom === 0) return '-';
    return formatPercentFromRatio(num / denom);
  };

  const rows: Array<{ label: string; getValue: (data: IncomeStatement) => number }> = [
    { label: 'Revenue', getValue: (data: IncomeStatement) => data.revenue },
    { label: 'Cost of Revenue', getValue: (data: IncomeStatement) => data.costOfRevenue },
    { label: 'Gross Profit', getValue: (data: IncomeStatement) => data.grossProfit },
    { label: 'Gross Margin %', getValue: (_data: IncomeStatement) => 0 },
    { label: 'R&D Expenses', getValue: (data: IncomeStatement) => data.researchAndDevelopmentExpenses },
    { label: 'SG&A Expenses', getValue: (data: IncomeStatement) => data.sellingGeneralAndAdministrativeExpenses },
    { label: 'Operating Expenses', getValue: (data: IncomeStatement) => data.operatingExpenses },
    { label: 'Operating Income', getValue: (data: IncomeStatement) => data.operatingIncome },
    { label: 'Operating Margin %', getValue: (_data: IncomeStatement) => 0 },
    { label: 'Interest Income', getValue: (data: IncomeStatement) => data.interestIncome },
    { label: 'Interest Expense', getValue: (data: IncomeStatement) => data.interestExpense },
    { label: 'Net Interest Income', getValue: (data: IncomeStatement) => data.netInterestIncome },
    { label: 'Other Income (Expense)', getValue: (data: IncomeStatement) => data.totalOtherIncomeExpensesNet },
    { label: 'Income Before Tax', getValue: (data: IncomeStatement) => data.incomeBeforeTax },
    { label: 'Tax Provision', getValue: (data: IncomeStatement) => data.incomeTaxExpense },
    { label: 'Tax Rate %', getValue: (_data: IncomeStatement) => 0 },
    { label: 'Net Income', getValue: (data: IncomeStatement) => data.netIncome },
    { label: 'Net Margin %', getValue: (_data: IncomeStatement) => 0 },
    { label: 'EPS (Diluted)', getValue: (data: IncomeStatement) => data.epsDiluted },
    { label: 'Shares Outstanding (Average Diluted)', getValue: (data: IncomeStatement) => data.weightedAverageShsOutDil }
  ];

  const hasFiscalValue = (getter: (data: IncomeStatement) => number) => {
    const all = [...annualData, ...quarterlyData, ...(ttmData ? [ttmData] : [])];
    return all.some((entry) => Number.isFinite(getter(entry)));
  };

  const hasAnyFiniteValue = (getter: (data: IncomeStatement) => number) => {
    const all = [...annualData, ...quarterlyData, ...(ttmData ? [ttmData] : [])];
    return all.some((entry) => {
      const value = getter(entry);
      return Number.isFinite(value);
    });
  };

  const boldRowLabels = new Set<string>([
    'Revenue',
    'Gross Profit',
    'Operating Income',
    'Income Before Tax',
    'Net Income',
    'EPS (Diluted)',
  ]);

  const annualSortedDesc = [...annualData]
    .filter((d) => d?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const annualPeriods = annualSortedDesc
    .slice(0, 10)
    .reverse();

  const quarterlySortedDesc = [...quarterlyData]
    .filter((d) => d?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const quarterlyPeriods = quarterlySortedDesc
    .slice(0, 5)
    .reverse();

  const annualDateSet = useMemo(() => {
    const set = new Set<string>();
    annualPeriods.forEach((row) => {
      if (row?.date) set.add(row.date);
    });
    return set;
  }, [annualPeriods]);

  const latestAnnualYear = useMemo(() => {
    if (annualPeriods.length === 0) return null;
    const last = annualPeriods[annualPeriods.length - 1]?.date;
    if (!last) return null;
    const y = new Date(last).getFullYear();
    return Number.isFinite(y) ? y : null;
  }, [annualPeriods]);

  const latestAnnualDateKey = useMemo(() => {
    if (annualPeriods.length === 0) return null;
    return annualPeriods[annualPeriods.length - 1]?.date ?? null;
  }, [annualPeriods]);

  const latestQuarterDateForAnnualYear = useMemo(() => {
    if (latestAnnualYear == null) return null;
    const matches = quarterlyPeriods.filter((q) => {
      const y = new Date(q.date).getFullYear();
      return Number.isFinite(y) && y === latestAnnualYear;
    });
    if (matches.length === 0) return null;
    return matches[matches.length - 1]?.date ?? null;
  }, [latestAnnualYear, quarterlyPeriods]);

  const getHighlightedQuarterDate = useCallback(
    (quarters: Array<{ date: string }>) => {
      if (latestAnnualDateKey) {
        const match = quarters.find((q) => q.date === latestAnnualDateKey);
        if (match) return latestAnnualDateKey;
      }
      return latestQuarterDateForAnnualYear;
    },
    [latestAnnualDateKey, latestQuarterDateForAnnualYear]
  );

  const highlightedQuarterDate = useMemo(
    () => getHighlightedQuarterDate(quarterlyPeriods),
    [getHighlightedQuarterDate, quarterlyPeriods]
  );

  const hasAnyFiniteValueInView = (getter: (data: IncomeStatement) => number) => {
    const viewRows = [...annualPeriods, ...quarterlyPeriods, ...(ttmData ? [ttmData] : [])];
    return viewRows.some((entry) => {
      const value = getter(entry);
      return Number.isFinite(value);
    });
  };

  const filteredRows = rows.filter((row) => {
    if ((row.label === 'Interest Income' || row.label === 'Interest Expense') && !hasAnyFiniteValueInView(row.getValue)) {
      return false;
    }
    if (!isFiscalSource) return true;
    if (row.label === 'Operating Expenses and Net Interest Income') return false;
    if (row.label === 'Net Interest Income') return false;
    if (row.label === 'Operating Expenses') return false;
    return true;
  });

  const containerStyle: React.CSSProperties = {
    padding: '15px 2rem 2rem 2rem',
    minHeight: '100vh',
    backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000',
    fontFamily: 'Manrope, Arial, sans-serif'
  };

  const tableContainerStyle: React.CSSProperties = {
    width: '95.3%',
    marginLeft: '1rem',
    padding: '0 0.5rem 0.5rem 0.5rem',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '75vh',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    position: 'relative'
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  };

  const floatingSourceLabelStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '0.35rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
    pointerEvents: 'none',
    zIndex: 8,
    padding: '0.1rem 0.35rem',
    background: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '6px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e5e7eb'}`
  };

  const groupHeaderRowTopOffsetPx = 30;

  const headerCellStyle: React.CSSProperties = {
    padding: '1rem',
    textAlign: 'center',
    borderBottom: `2px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    backgroundColor: isDarkMode ? '#1F2937' : '#f5f5f5',
    fontWeight: 500,
    fontSize: '0.88rem'
  };

  const ttmHeaderStyle: React.CSSProperties = {
    ...headerCellStyle,
    borderLeft: `3px solid ${isDarkMode ? '#555' : '#999'}`
  };

  const stickyHeaderCellStyle: React.CSSProperties = {
    ...headerCellStyle,
    position: 'sticky',
    top: groupHeaderRowTopOffsetPx,
    zIndex: 3,
  };

  const stickyHeaderFirstCellStyle: React.CSSProperties = {
    ...stickyHeaderCellStyle,
    left: 0,
    zIndex: 4,
  };

  const stickyTtmHeaderStyle: React.CSSProperties = {
    ...ttmHeaderStyle,
    position: 'sticky',
    top: groupHeaderRowTopOffsetPx,
    zIndex: 3,
  };

  const stickyGroupHeaderCellStyle: React.CSSProperties = {
    background: isDarkMode ? '#111827' : '#fff',
    height: `${groupHeaderRowTopOffsetPx}px`,
    fontSize: '14px',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    fontWeight: 300,
    border: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 5,
  };

  const stickyGroupHeaderLeftCellStyle: React.CSSProperties = {
    ...stickyGroupHeaderCellStyle,
    left: 0,
    zIndex: 6,
  };

  const headerLinkStyle: React.CSSProperties = {
    color: isDarkMode ? '#93C5FD' : '#1D4ED8',
    textDecoration: 'underline',
    cursor: 'pointer',
  };

  const headerLinkButtonStyle: React.CSSProperties = {
    ...headerLinkStyle,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  };


  const cellStyle: React.CSSProperties = {
    padding: '1rem',
    borderBottom: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderLeft: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    textAlign: 'center'
  };

  const firstColumnStyle: React.CSSProperties = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
    position: 'sticky',
    left: 0,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    zIndex: 1,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`
  };

  const ttmCellStyle: React.CSSProperties = {
    ...cellStyle,
    borderLeft: `3px solid ${isDarkMode ? '#555' : '#999'}`
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerCellStyle}>Income Statement</div>
        <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight="240px" />

        <FloatingNewNoteButton
          visible={showFloatingButton}
          onClick={openNewNote}
          label="New Note"
          backgroundColor={isDarkMode ? '#374151' : undefined}
        />

        <ResearchNoteModal
          isOpen={isNoteModalOpen}
          onRequestClose={() => setIsNoteModalOpen(false)}
          isDarkMode={isDarkMode}
          noteTitle={noteTitle}
          onChangeTitle={setNoteTitle}
          noteCategory={noteCategory}
          onChangeCategory={setNoteCategory}
          noteContent={noteContent}
          onChangeContent={setNoteContent}
          categories={RESEARCH_DESK_NOTE_CATEGORIES}
          canSubmit={canAddNote}
          onSubmit={async () => {
            if (!canPersist) {
              onNotify?.('Please login to save notes.', 'error');
              return;
            }

            const title = extractTitleFromContent(noteContent);
            if (!title) return;
            const associatedWith = noteCategory.trim();
            if (!associatedWith) return;

            try {
              await adminApi.createResearchDeskNote(authToken as string, {
                userId: authUserId as number,
                title,
                associatedWith,
                stockSymbol: selectedStock,
                html: noteContent ?? '',
              });
              setIsNoteModalOpen(false);
              setNoteTitle('');
              setNoteContent('');
              setNoteCategory('Income Statement');
              onNotify?.(`${selectedStock} • Income Statement • Saved`, 'success');
            } catch (e: any) {
              if (isUnauthorized(e)) onUnauthorized?.();
            }
          }}
        />
      </div>
    );
  }

  return (
    <div id="income-statement-fiscal-container" style={containerStyle}>
      <div id="income-statement-fiscal-table-wrapper" style={tableContainerStyle}>
        {showSourceLabels ? (
          <div style={floatingSourceLabelStyle}>{isFiscalSource ? 'fiscal.ai' : 'FMP'}</div>
        ) : null}
        <table id="income-statement-fiscal-table" style={tableStyle}>
          <thead>
            <tr>
              <th style={stickyGroupHeaderLeftCellStyle}></th>
              <th
                colSpan={annualPeriods.length}
                style={{
                  ...stickyGroupHeaderCellStyle,
                  textAlign: 'left',
                  padding: '5px',
                }}
              >
                Annuals
              </th>
              <th
                style={{
                  ...stickyGroupHeaderCellStyle,
                  textAlign: 'center',
                  padding: '5px',
                }}
              ></th>
              <th
                colSpan={quarterlyPeriods.length}
                style={{
                  ...stickyGroupHeaderCellStyle,
                  textAlign: 'left',
                  padding: '1px',
                }}
              >
                Quarterly
              </th>
            </tr>
            <tr>
              <th style={{ ...stickyHeaderFirstCellStyle, textAlign: 'center' }}>Metric</th>
              {annualPeriods.map((data, idx) => (
                <th
                  key={`annual-${idx}`}
                  style={{
                    ...stickyHeaderCellStyle,
                    fontWeight: latestAnnualYear != null && new Date(data.date).getFullYear() === latestAnnualYear ? 800 : stickyHeaderCellStyle.fontWeight,
                  }}
                >
                  {(() => {
                    const year = new Date(data.date).getFullYear();
                    const label = formatDate(data.date);
                    const directFilingId =
                      (Number.isFinite(year) ? fiscalAnnualFilingIdByYear[year] : undefined) ??
                      fiscalAnnualFilingIdByLabel[label];
                    return (
                      <button
                        type="button"
                        onClick={() => openAnnualFilingForYear(year, directFilingId)}
                        style={headerLinkButtonStyle}
                      >
                        {formatDate(data.date)}
                      </button>
                    );
                  })()}
                </th>
              ))}
              <th style={stickyTtmHeaderStyle}>TTM</th>
              {quarterlyPeriods.map((data, idx) => (
                <th
                  key={`quarter-${idx}`}
                  style={{
                    ...stickyHeaderCellStyle,
                    fontWeight: highlightedQuarterDate && data.date === highlightedQuarterDate ? 800 : stickyHeaderCellStyle.fontWeight,
                  }}
                >
                  {(() => {
                    const label = formatDate(data.date);
                    const year = new Date(data.date).getFullYear();
                    const isAnnualLike = annualDateSet.has(data.date);
                    const annualFallbackId =
                      (Number.isFinite(year) ? fiscalAnnualFilingIdByYear[year] : undefined) ??
                      fiscalAnnualFilingIdByLabel[label];
                    const directFilingId = isAnnualLike ? annualFallbackId : fiscalQuarterFilingIdByLabel[label];
                    return (
                      <button
                        type="button"
                        onClick={() => openQuarterFilingForDate(data.date, directFilingId, isAnnualLike ? '10-K' : '10-Q')}
                        style={headerLinkButtonStyle}
                      >
                        {formatDate(data.date)}
                      </button>
                    );
                  })()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {(() => {
                  const isBoldRow = boldRowLabels.has(row.label);
                  const isGrossMarginRow = row.label === 'Gross Margin %';
                  const isOperatingMarginRow = row.label === 'Operating Margin %';
                  const isTaxRateRow = row.label === 'Tax Rate %';
                  const isNetMarginRow = row.label === 'Net Margin %';
                  const isPercentRow = isGrossMarginRow || isOperatingMarginRow || isTaxRateRow || isNetMarginRow;
                  const firstStyle: React.CSSProperties = isBoldRow
                    ? { ...firstColumnStyle, fontWeight: 700 }
                    : firstColumnStyle;
                  const dataStyle: React.CSSProperties = isBoldRow
                    ? { ...cellStyle, fontWeight: 700 }
                    : cellStyle;
                  const ttmStyle: React.CSSProperties = isBoldRow
                    ? { ...ttmCellStyle, fontWeight: 700 }
                    : ttmCellStyle;

                  const renderAnnualCell = (d: IncomeStatement) => {
                    if (!isPercentRow) {
                      if (isFiscalSource && row.label === 'Interest Expense') {
                        return formatNumberAbs(row.getValue(d));
                      }
                      return formatNumber(row.getValue(d));
                    }
                    if (isFiscalSource) {
                      if (isGrossMarginRow) return formatPercentFromValue(d.grossProfit, d.revenue);
                      if (isOperatingMarginRow) return formatPercentFromValue(d.operatingIncome, d.revenue);
                      if (isNetMarginRow) return formatPercentFromValue(d.netIncome, d.revenue);
                      if (isTaxRateRow) {
                        const ratio = getFiscalTaxRateByDate(d.date, 'annual');
                        return typeof ratio === 'number' ? formatPercentMaybeRatio(ratio) : '-';
                      }
                      return '-';
                    }
                    const ratio = getRatioByDate(d.date, 'annual');
                    if (isGrossMarginRow) {
                      const gm = (ratio as any)?.grossProfitMargin ?? (ratio as any)?.grossMargin ?? (ratio as any)?.grossProfitMarginRatio;
                      return formatPercentMaybeRatio(gm);
                    }
                    if (isTaxRateRow) {
                      const pretax = d?.incomeBeforeTax;
                      const tax = d?.incomeTaxExpense;
                      if (typeof pretax !== 'number' || pretax === 0 || typeof tax !== 'number') return '-';
                      return formatPercentFromRatio(tax / pretax);
                    }
                    if (isNetMarginRow) {
                      const nm = (ratio as any)?.netProfitMargin ?? (ratio as any)?.netMargin ?? (ratio as any)?.netProfitMarginRatio;
                      return formatPercentMaybeRatio(nm);
                    }
                    const om = (ratio as any)?.operatingProfitMargin ?? (ratio as any)?.operatingMargin ?? (ratio as any)?.operatingProfitMarginRatio;
                    return formatPercentMaybeRatio(om);
                  };

                  const renderQuarterCell = (d: IncomeStatement) => {
                    if (!isPercentRow) {
                      if (isFiscalSource && row.label === 'Interest Expense') {
                        return formatNumberAbs(row.getValue(d));
                      }
                      return formatNumber(row.getValue(d));
                    }
                    if (isFiscalSource) {
                      if (isGrossMarginRow) return formatPercentFromValue(d.grossProfit, d.revenue);
                      if (isOperatingMarginRow) return formatPercentFromValue(d.operatingIncome, d.revenue);
                      if (isNetMarginRow) return formatPercentFromValue(d.netIncome, d.revenue);
                      if (isTaxRateRow) {
                        const ratio = getFiscalTaxRateByDate(d.date, 'quarter');
                        return typeof ratio === 'number' ? formatPercentMaybeRatio(ratio) : '-';
                      }
                      return '-';
                    }
                    const ratio = getRatioByDate(d.date, 'quarter');
                    if (isGrossMarginRow) {
                      const gm = (ratio as any)?.grossProfitMargin ?? (ratio as any)?.grossMargin ?? (ratio as any)?.grossProfitMarginRatio;
                      return formatPercentMaybeRatio(gm);
                    }
                    if (isTaxRateRow) {
                      const pretax = d?.incomeBeforeTax;
                      const tax = d?.incomeTaxExpense;
                      if (typeof pretax !== 'number' || pretax === 0 || typeof tax !== 'number') return '-';
                      return formatPercentFromRatio(tax / pretax);
                    }
                    if (isNetMarginRow) {
                      const nm = (ratio as any)?.netProfitMargin ?? (ratio as any)?.netMargin ?? (ratio as any)?.netProfitMarginRatio;
                      return formatPercentMaybeRatio(nm);
                    }
                    const om = (ratio as any)?.operatingProfitMargin ?? (ratio as any)?.operatingMargin ?? (ratio as any)?.operatingProfitMarginRatio;
                    return formatPercentMaybeRatio(om);
                  };

                  const renderTtmCell = () => {
                    if (!isPercentRow) {
                      if (!ttmData) return '-';
                      if (isFiscalSource && row.label === 'Interest Expense') {
                        return formatNumberAbs(row.getValue(ttmData));
                      }
                      return formatNumber(row.getValue(ttmData));
                    }
                    const rev = ttmData?.revenue;
                    if (typeof rev !== 'number' || rev === 0) return '-';
                    if (isGrossMarginRow) {
                      const gp = ttmData?.grossProfit;
                      if (typeof gp !== 'number') return '-';
                      return formatPercentFromRatio(gp / rev);
                    }
                    if (isTaxRateRow) {
                      const ratioLtm = ttmData?.date ? getFiscalTaxRateByDate(ttmData.date, 'ltm') : undefined;
                      const ratio =
                        typeof ratioLtm === 'number'
                          ? ratioLtm
                          : ttmData?.date
                            ? getFiscalTaxRateByDate(ttmData.date, 'quarter')
                            : undefined;
                      return typeof ratio === 'number' ? formatPercentMaybeRatio(ratio) : '-';
                    }
                    if (isNetMarginRow) {
                      const ni = ttmData?.netIncome;
                      if (typeof ni !== 'number') return '-';
                      return formatPercentFromRatio(ni / rev);
                    }
                    const oi = ttmData?.operatingIncome;
                    if (typeof oi !== 'number') return '-';
                    return formatPercentFromRatio(oi / rev);
                  };

                  return (
                    <>
                      <td style={firstStyle}>{row.label}</td>
                      {annualPeriods.map((data, idx) => (
                        (() => {
                          const text = renderAnnualCell(data);
                          return (
                        <td key={`annual-${rowIdx}-${idx}`} style={dataStyle}>
                          <span style={withNegativeColor({}, text)}>{text}</span>
                        </td>
                          );
                        })()
                      ))}
                      {(() => {
                        const text = renderTtmCell();
                        return (
                          <td style={withNegativeColor(ttmStyle, text)}>
                            {text}
                          </td>
                        );
                      })()}
                      {quarterlyPeriods.map((data, idx) => (
                        (() => {
                          const text = renderQuarterCell(data);
                          return (
                            <td key={`quarter-${rowIdx}-${idx}`} style={withNegativeColor(dataStyle, text)}>
                              {text}
                            </td>
                          );
                        })()
                      ))}
                    </>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DataSourceAttribution isDarkMode={isDarkMode} />

      <FloatingNewNoteButton
        visible={showFloatingButton}
        onClick={openNewNote}
        label="New Note"
        backgroundColor={isDarkMode ? '#374151' : undefined}
      />

      <ResearchNoteModal
        isOpen={isNoteModalOpen}
        onRequestClose={() => setIsNoteModalOpen(false)}
        isDarkMode={isDarkMode}
        noteTitle={noteTitle}
        onChangeTitle={setNoteTitle}
        noteCategory={noteCategory}
        onChangeCategory={setNoteCategory}
        noteContent={noteContent}
        onChangeContent={setNoteContent}
        categories={RESEARCH_DESK_NOTE_CATEGORIES}
        canSubmit={canAddNote}
        onSubmit={async () => {
          if (!canPersist) {
            onNotify?.('Please login to save notes.', 'error');
            return;
          }

          const title = extractTitleFromContent(noteContent);
          if (!title) return;
          const associatedWith = noteCategory.trim();
          if (!associatedWith) return;

          try {
            await adminApi.createResearchDeskNote(authToken as string, {
              userId: authUserId as number,
              title,
              associatedWith,
              stockSymbol: selectedStock,
              html: noteContent ?? '',
            });
            setIsNoteModalOpen(false);
            setNoteTitle('');
            setNoteContent('');
            setNoteCategory('Income Statement');
            onNotify?.(`${selectedStock} • Income Statement • Saved`, 'success');
          } catch (e: any) {
            if (isUnauthorized(e)) onUnauthorized?.();
          }
        }}
      />
    </div>
  );
};

export default ResearchIncomeStatementFiscalScreen;

